import React, {
  useCallback, useEffect, useMemo, useState, Fragment, ReactElement,
} from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router';
import PageTabs from '../../../../components/page-tabs/page-tabs.component';
import StatusBar from '../../../../components/status-bar/status-bar.component';
import Loader from '../../../../components/loader/loader.component';
import FormError from '../../../../components/form-error/form-error.component';
import PullToRefresh from '../../../../components/pull-to-refresh/pull-to-refresh.component';
import ContentHeader from '../../../../components/content-header/content-header.component';
import ConfirmModal from '../../../../components/confirm-modal/confirm-modal.component';
import LeaveConfirmModal from '../../../../components/leave-confirm-modal/leave-confirm-modal.component';
import VerifyPhoneModal from '../../../../components/verify-phone-modal/verify-phone-modal.component';
import LimitedPageContainer from '../../../../components/limited-page-block/limited-page-container.component';
import PageContainer from '../../../../components/page-container/page-container.component';
import PageContainerSide from '../../../../components/page-container-side/page-container-side.component';
import PageContainerMain from '../../../../components/page-container-main/page-container-main.component';
import ImageContainer from '../../../../components/image-container/image-container.component';
import Controls from '../../../app/controls/controls.component';
import SettingsPageTabs from '../../settings-page-tabs/settings-page-tabs.component';
import SettingsUserGeneral from './settings-user-general/settings-user-general.component';
import SettingsUserSecuritySummary
  from './settings-user-security/settings-user-security-summary/settings-user-security-summary.component';
import SettingsUserAccountActivity
  from './settings-user-security/settings-user-account-activity/settings-user-account-activity.component';
import SettingsUserNotifications from './settings-user-notifications/settings-user-notifications.component';
import FormatService from '../../../../services/format-service';
import ScreenService from '../../../../services/screen-service';
import GoogleMapService from '../../../../services/google-map-service';
import StructureService from '../../../../services/structure-service';
import FormService from '../../../../services/form-service';
import useEditModeQuery from '../../../../hooks/use-edit-mode-query';
import useFormBlock from '../../../../hooks/use-form-block';
import useServerFormErrors from '../../../../hooks/use-server-form-errors';
import { toggleEditMode } from '../../../app/controls/controls.action';
import { closeUserSessions } from '../../../app/content/logout/logout.action';
import {
  getUserApiKey, exportUserSettings, importUserSettings, resetUserSettings, resetUserSettingsExportFile,
  resetUserApiKey,
} from '../../settings.action';
import { deleteUser } from '../../settings-user-list/settings-user-list.action';
import {
  deleteOtpSecretData, generateOtpSecretData, getOtpSecretData, saveUserSettingsData,
  sendPhoneVerificationCode, verifyUserOtp, verifyUserPhone,
} from '../settings-user.action';
import saveProfileSettings from '../../settings-profile/settings-profile.action';
import {
  SETTINGS_PROFILE_EDIT, SETTINGS_USER_EDIT, SETTINGS_USER_DELETE,
} from '../../../../constants/entity/permissions';
import { ROUTE_USER, ROUTE_SETTINGS_USER_LIST } from '../../../../constants/system/routes';
import { QUERY_PARAM_TAB } from '../../../../constants/system/query-params';
import { ACCOUNT_USER_TYPE_SYSTEM_ADMIN } from '../../../../constants/entity/account-user-types';
import {
  SETTING_TYPE_NOTIFICATION_SETTINGS, SETTING_TYPE_USER_SETTINGS,
} from '../../../../constants/entity/setting-types';
import {
  SETTING_NAME_DEFAULT_PORTFOLIO_FILTER_IDS, SETTING_NAME_SEARCH_RESULT_GROUPS,
} from '../../../../constants/entity/setting-names';
import {
  TAB_NAME_NOTIFICATIONS, TAB_NAME_GENERAL, TAB_NAME_SECURITY, TAB_NAME_ACCOUNT_ACTIVITY,
} from '../../../../constants/system/tab-names';
import { SEARCH_RESULT_GROUP_LIST } from '../../../../constants/system/search-result-groups';
import {
  ACCOUNT_USER_TYPE_LIST_ADMIN, ACCOUNT_USER_TYPE_LIST_DEAL_LIMITED, ACCOUNT_USER_TYPE_LIST_PORTFOLIO_LIMITED,
} from '../../../../constants/entity/account-user-type-lists';
import { TAB_INDEX_DEFAULT } from '../../../../constants/system/tab-indexes';
import { TIMEOUT_SHORT } from '../../../../constants/system/timeouts';
import { VERIFICATION_TYPE_AUTHENTICATOR_APP_OTP } from '../../../../constants/entity/verification-types';
import PersonDefaultImage from '../../../../static/images/person-default-image.png';

const SettingsUserInternal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isEditMode,
    isRightDrawerOpen,
    isDataLoaded,
    isUserProfile,
    pageTitle,
    activeTab,
    userId,
    userData,
    userSettingsExportFile,
    currentUser,
    actionHash,
    userApiKey,
    otpSecretHash,
    otpSecretQrCodeLink,
    assetTypeList,
    accountUserTypeList,
    verificationTypeList,
    accessLevelList,
    contactCompanyList,
    dealList,
    portfolioList,
    hiddenPortfolioList,
    routeList,
    roleList,
    onRefresh,
    onDealSearch,
    onDealSearchClose,
    onContactCompanySearch,
    onContactCompanySearchClose,
    submitForm,
    isFormPristine,
    isFormStructureModified,
    isFormSaving,
    formProps,
    formError,
    formValues,
    withFormErrors,
    serverFormErrors,
    mainServerFormError,
    resetForm,
    changeForm,
  } = props;

  const navigate: ReactRouterNavigate = useNavigate();
  const location: ReactRouterLocation = useLocation();
  const dispatch: ReduxDispatch = useDispatch();

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryTab: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAB]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TAB].toString()) : null;
  const editPermission: string = isUserProfile ? SETTINGS_PROFILE_EDIT : SETTINGS_USER_EDIT;
  const userSettingsData: Record<string, SettingValue> | null = userData && userData.settings
  && userData.settings.length ? StructureService.getItemMap(
    userData.settings.filter((userSetting: Setting): boolean => userSetting.type === SETTING_TYPE_USER_SETTINGS),
    'name',
    'value',
  ) : null;
  const userSettingsTotal: number | null = userSettingsData && Object.keys(userSettingsData).length;

  const userApiKeyValue: string | null = userApiKey || (userData && userData.apiKey) || null;
  const userIsPhoneVerified: boolean = (!!userData && userData.isPhoneVerified) || false;
  const isCurrentUser: boolean = (!!userData && currentUser.id === userData.id) || false;
  const isUserAdmin: boolean = !!userData && !!userData.accountUserType
    && ACCOUNT_USER_TYPE_LIST_ADMIN.includes(userData.accountUserType);
  const isCurrentUserSystemAdmin: boolean = !!currentUser && !!currentUser.accountUserType
    && currentUser.accountUserType === ACCOUNT_USER_TYPE_SYSTEM_ADMIN;
  const withNoEditMode: boolean = (!isCurrentUser && isUserAdmin && !isCurrentUserSystemAdmin)
    || (queryTab === TAB_NAME_ACCOUNT_ACTIVITY);

  const [isUserPhotoDeleted, setIsUserPhotoDeleted]: [boolean, Function] = useState(false);
  const [userPhotoFile, setUserPhotoFile]: [File | null, Function] = useState(null);
  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [userAssetTypes, setUserAssetTypes]: [AssetType[], Function] = useState([]);
  const [userDeals, setUserDeals]: [Deal[], Function] = useState([]);
  const [userPortfolios, setUserPortfolios]: [Portfolio[], Function] = useState([]);
  const [isCloseUserSessionsConfirmModalOpen, setIsCloseUserSessionsConfirmModalOpen]: [boolean, Function]
    = useState(false);
  const [isResetSettingsConfirmModalOpen, setIsResetSettingsConfirmModalOpen]: [boolean, Function] = useState(false);
  const [isVerifyPhoneModalOpen, setIsVerifyPhoneModalOpen]: [boolean, Function] = useState(false);
  const [verifyPhoneModalErrorMessage, setVerifyPhoneModalErrorMessage]: [string | null, Function] = useState(null);
  const [userPhone, setUserPhone]: [string | null, Function] = useState(null);
  const [importExportErrorMessage, setImportExportErrorMessage]: [string | null, Function] = useState(null);
  const [connectOtpModalErrorMessage, setConnectOtpModalErrorMessage]: [string | null, Function] = useState(null);
  const [selectedNotificationDeals, setSelectedNotificationDeals]: [Deal[] | null, Function]
    = useState<StateValueDefault>(null);
  const [selectedNotificationPortfolios, setSelectedNotificationPortfolios]: [Portfolio[] | null, Function]
    = useState<StateValueDefault>(null);
  const [notificationVisibleOptions, setNotificationVisibleOptions]: [number[] | null, Function] = useState(null);
  const [selectedDefaultPortfolios, setSelectedDefaultPortfolios]: [Portfolio[] | null, Function] = useState(null);
  const [selectedSearchResultGroups, setSelectedSearchResultGroups]: [SearchResultGroup[] | null, Function]
    = useState(null);
  const [isUserPhoneVerified, setIsUserPhoneVerified]: [boolean | null, Function] = useState(null);
  const [isLeaveConfirmed, setIsLeaveConfirmed]: [boolean, Function] = useState(false);
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);
  const [lastLocation, setLastLocation]: [ReactRouterLocation | null, Function] = useState<StateValueDefault>(null);
  const [lastTabIndex, setLastTabIndex]: [number | null, Function] = useState(null);
  const [currentUserApiKey, setCurrentUserApiKey]: [string | null, Function] = useState<StateValueDefault>(null);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen]: [boolean, Function] = useState(false);
  const [confirmModalErrorMessage, setConfirmModalErrorMessage]: [string | null, Function] = useState(null);

  const editPermissions: string[] = useMemo((): string[] => [editPermission], [editPermission]);

  const userNotificationSettingsData: Setting[] = useMemo((): Setting[] => (
    userData && userData.settings && userData.settings.length ? userData.settings
      .filter((userSetting: Setting): boolean => userSetting.type === SETTING_TYPE_NOTIFICATION_SETTINGS) : []
  ), [userData]);
  const commonDealList: Deal[] = userData && userData.accountUserDeals && userData.accountUserDeals.length
    ? userData.accountUserDeals : dealList;
  const commonPortfolioList: Portfolio[] = userData && userData.accountUserPortfolios
  && userData.accountUserPortfolios.length ? userData.accountUserPortfolios : portfolioList;

  const isUserApiKeyChanged: boolean = !!userApiKey && (!userData || (!!userData && userData.apiKey !== userApiKey));
  const isFormDataPristine: boolean = isFormPristine && !userPhotoFile && !userAssetTypes.length && !userDeals.length
    && !userPortfolios.length && !isUserPhotoDeleted && !isUserApiKeyChanged;
  const isFormChanged: boolean = isEditMode && !isFormDataPristine;
  const isFormStateChanged: boolean = isEditMode || isUserApiKeyChanged;
  const isSaveDisabled: boolean = isFormSaving || (isFormDataPristine && !isFormStructureModified);
  const isEditModeQueryDisabled: boolean = isLeaveConfirmed && (!!lastLocation || (lastTabIndex !== null
    && lastTabIndex >= TAB_INDEX_DEFAULT));
  const closeUserSessionsConfirmModalTitle: string = 'Terminate All Other Sessions';
  const closeUserSessionsConfirmModalText: string = 'Are you sure you want to terminate sessions on all other devices?';
  const resetSettingsConfirmModalTitle: string = 'Reset Settings';
  const resetSettingsConfirmModalText: string = 'Are you sure you want to restore settings to defaults?';
  const deleteConfirmModalTitle: string = 'Delete User';
  const deleteConfirmModalText: string = 'Are you sure you want to delete this user?';

  const onCancelForm: Function = useCallback((): void => {
    if (resetForm) {
      FormService.forceResetForm(resetForm, formProps);
    }

    setUserPhotoFile(null);
    setIsUserPhotoDeleted(false);
    setUserDeals([]);
    setUserPortfolios([]);
    setUserAssetTypes([]);
    setIsUserPhoneVerified(userIsPhoneVerified || false);
    resetSelectedNotificationDeals();
    resetSelectedNotificationPortfolios();
    resetNotificationVisibleOptions();
    dispatch(toggleEditMode(false));

    if (isUserApiKeyChanged) {
      dispatch(resetUserApiKey());
    }
  }, [isUserPhoneVerified, isUserApiKeyChanged, userNotificationSettingsData]);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmed(true);
    setIsLeaveConfirmModalOpen(false);

    onCancelForm();
  }, [isUserPhoneVerified, isUserApiKeyChanged]);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const onAddressSearchCompleted: Function = useCallback((addressObject: JsonObject): void => {
    if (changeForm) {
      changeForm('address', addressObject);
      changeForm('location', GoogleMapService.extractLocation(addressObject));
      changeForm('postalCode', GoogleMapService.extractPostalCode(addressObject));
      changeForm('streetAddress', GoogleMapService.extractStreetAddress(addressObject));
    }
  }, []);

  const onAddressFieldKeyDown: Function = useCallback((): void => {
    if (changeForm) {
      changeForm('address', '');
      changeForm('streetAddress', '');
      changeForm('location', '');
      changeForm('postalCode', '');
    }
  }, []);

  const onAssetTypesChange: Function = useCallback((assetTypeValues: AssetType): void => {
    if (changeForm) {
      changeForm('assetTypes', assetTypeValues);
    }
  }, []);

  const onAccountUserTypeChange: Function = useCallback((
    accountUserTypeName: string,
    accountUserTypeGroupName: string,
  ): void => {
    if (changeForm) {
      if (!ACCOUNT_USER_TYPE_LIST_DEAL_LIMITED.includes(accountUserTypeName)) {
        changeForm('accountUserDeals', []);
      }

      if (!ACCOUNT_USER_TYPE_LIST_PORTFOLIO_LIMITED.includes(accountUserTypeName)) {
        changeForm('accountUserPortfolios', []);
      }

      changeForm('accountUserTypeGroup', accountUserTypeGroupName || null);
    }
  }, []);

  const onAccountUserPortfoliosChange: Function = useCallback((portfolioValues: Portfolio[]): void => {
    setUserPortfolios(portfolioValues);

    if (changeForm) {
      changeForm('accountUserPortfolios', portfolioValues);
      changeForm('accountUserDealAccessLevels', null);
    }
  }, []);

  const onAccountUserDealsChange: Function = useCallback((dealValues: Deal[], removedDealValues: Deal[]): void => {
    setUserDeals(dealValues);

    if (changeForm) {
      changeForm('accountUserDeals', dealValues);

      if (removedDealValues && removedDealValues.length) {
        removedDealValues.forEach((dealValue: Deal): void => {
          const formKeyDeal: string = FormService.getIndexedFormFieldKey(dealValue.id);

          changeForm(`accountUserDealAccessLevels[${formKeyDeal}]`, null);
        });
      }
    }
  }, []);

  const onNotificationSettingOptionDealsChange: Function = useCallback((
    settingName: string, dealValues: Deal[],
  ): void => {
    if (changeForm) {
      changeForm(`${settingName}.options.dealIds`, dealValues);
    }
  }, []);

  const onNotificationSettingOptionPortfoliosChange: Function = useCallback((
    settingName: string, portfolioValues: Portfolio[],
  ): void => {
    if (changeForm) {
      changeForm(`${settingName}.options.portfolioIds`, portfolioValues);
    }
  }, []);

  const onUserPhotoChange: Function = useCallback((photoValue: File): void => {
    if (changeForm) {
      changeForm('photo', photoValue);
    }
  }, []);

  const onAccountUserDealAccessLevelChange: Function = useCallback((
    dealId: number, accessLevelValue: AccessLevel,
  ): void => {
    const formKeyDeal: string = FormService.getIndexedFormFieldKey(dealId);

    if (changeForm) {
      changeForm(`accountUserDealAccessLevels[${formKeyDeal}]`, accessLevelValue);
    }
  }, []);

  const onPhoneChange: Function = useCallback((phoneValue?: string): void => {
    setIsUserPhoneVerified(false);

    if (changeForm) {
      changeForm('phone', phoneValue);
      changeForm('isPhoneVerified', false);
    }
  }, []);

  const onPhoneWorkChange: Function = useCallback((phoneWorkValue?: string): void => {
    if (changeForm) {
      changeForm('phoneWork', phoneWorkValue);
    }
  }, []);

  const onVerifyPhone: Function = useCallback((phone: string): void => {
    if (!phone || !FormatService.isValidPhoneNumber(phone)) {
      return;
    }

    setIsVerifyPhoneModalOpen(true);
    setUserPhone(phone);

    dispatch(sendPhoneVerificationCode({
      phone,
      userId,
    }, undefined, (exception: ServerError): void => {
      if (exception && exception.message) {
        setVerifyPhoneModalErrorMessage(exception.message);
      }
    }));
  }, [userId]);

  const openDeleteConfirmModal: Function = useCallback((): void => {
    setIsDeleteConfirmModalOpen(true);
  }, []);

  const closeDeleteConfirmModal: Function = useCallback((): void => {
    setIsDeleteConfirmModalOpen(false);
    setConfirmModalErrorMessage(null);
  }, []);

  const onDeleteClick: Function = useCallback((): void => {
    openDeleteConfirmModal();
  }, []);

  const onDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!userId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteUser({
        userId,
      }, (response: ActionResponse): void => {
        setIsDeleteConfirmModalOpen(false);
        setConfirmModalErrorMessage(null);

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        setTimeout((): void => {
          navigate({
            pathname: ROUTE_SETTINGS_USER_LIST,
          });
        }, TIMEOUT_SHORT);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [userId]);

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const userFormItem: FormValueItem = {
      ...values,
    };

    const contactCompany: Contact | null = userFormItem.companyId && contactCompanyList && contactCompanyList.length
      ? (contactCompanyList.find((contactCompanyItem: Contact): boolean => (
        userFormItem.companyId === contactCompanyItem.id
      )) || null) : null;

    const notificationSettingsForm: FormValueItem[] = userFormItem.notificationSettings
    && Object.keys(userFormItem.notificationSettings).length
      ? Object.keys(userFormItem.notificationSettings).map((settingName: string): FormValueItem => {
        const notificationSettingsFormItem: FormValueItem = userFormItem.notificationSettings[settingName];
        const settingsItem: FormValueItem = {
          name: settingName,
          value: notificationSettingsFormItem.value
            ? notificationSettingsFormItem.value.toString() : null,
          options: notificationSettingsFormItem.options ? {} : null,
        };

        if (settingsItem.options && notificationSettingsFormItem.options.dealIds) {
          settingsItem.options.dealIds = notificationSettingsFormItem.value
            ? notificationSettingsFormItem.options.dealIds
              .map((dealItem: Deal): number => dealItem.id) : [];
        }

        if (settingsItem.options && notificationSettingsFormItem.options.portfolioIds) {
          settingsItem.options.portfolioIds = notificationSettingsFormItem.value
            ? notificationSettingsFormItem.options.portfolioIds
              .map((portfolioItem: Portfolio): number => portfolioItem.id) : [];
        }

        return settingsItem;
      }) : [];

    delete userFormItem.notificationSettings;

    const userSettingsForm: FormValueItem[] = userFormItem.userSettings
    && Object.keys(userFormItem.userSettings).length
      ? Object.keys(userFormItem.userSettings).map((settingName: string): FormValueItem => {
        const userSettingValue: SettingValue = userFormItem.userSettings[settingName].value;

        let resultSettingValue: SettingValue | null;
        if (settingName === SETTING_NAME_DEFAULT_PORTFOLIO_FILTER_IDS) {
          resultSettingValue = ((userSettingValue && !!userSettingValue.length && FormatService.formatJsonString(
            userSettingValue.map((settingValueItem: SettingValue): number => settingValueItem.id),
          )) || null);
        } else if (settingName === SETTING_NAME_SEARCH_RESULT_GROUPS) {
          resultSettingValue = ((userSettingValue && !!userSettingValue.length && FormatService.formatJsonString(
            userSettingValue.map((settingValueItem: SearchResultGroup): string => settingValueItem.name),
          )) || null);
        } else {
          resultSettingValue = ((userSettingValue && userSettingValue.toString()) || null);
        }

        return {
          name: settingName,
          value: resultSettingValue,
        };
      }) : [];

    delete userFormItem.userSettings;

    if (userId) {
      userFormItem.id = userId;
    }

    delete userFormItem.passwordConfirm;

    if (!userFormItem.password) {
      delete userFormItem.password;
      delete userFormItem.oldPassword;
    }

    delete userFormItem.location;
    delete userFormItem.streetAddress;
    delete userFormItem.postalCode;
    delete userFormItem.accountUserTypeGroup;

    userFormItem.assetTypes = userFormItem.assetTypes && userFormItem.assetTypes.length
      ? userFormItem.assetTypes.map((assetTypeItem: AssetType): number => assetTypeItem.id) : [];
    userFormItem.accountUserDeals = userFormItem.accountUserDeals && userFormItem.accountUserDeals.length
      ? userFormItem.accountUserDeals.map((dealItem: Deal): number => dealItem.id) : [];
    userFormItem.accountUserPortfolios = userFormItem.accountUserPortfolios && userFormItem.accountUserPortfolios.length
      ? userFormItem.accountUserPortfolios.map((portfolioItem: Portfolio): number => portfolioItem.id) : [];
    userFormItem.accountUserDealAccessLevels = userFormItem.accountUserDealAccessLevels
    && Object.keys(userFormItem.accountUserDealAccessLevels).length
      ? Object.keys(userFormItem.accountUserDealAccessLevels).map((
        formKeyDeal: string,
      ): AccountUserDealAccessLevelInput => ({
        dealId: FormatService.parseInt(FormService.extractIndexedFormFieldKey(formKeyDeal) as string) as number,
        accessLevel: userFormItem.accountUserDealAccessLevels[formKeyDeal] || null,
      })).filter((accountUserDealAccessLevel: AccountUserDealAccessLevelInput): boolean => (
        userFormItem.accountUserDeals.includes(accountUserDealAccessLevel.dealId)
      )) : [];
    userFormItem.phone = userFormItem.phone && FormatService.isValidPhoneNumber(userFormItem.phone, true)
      ? FormatService.formatPhoneValue(userFormItem.phone) : null;
    userFormItem.phoneExtension = userFormItem.phoneExtension
      ? FormatService.parseInt(userFormItem.phoneExtension) : null;
    userFormItem.phoneWork = userFormItem.phoneWork && FormatService.isValidPhoneNumber(userFormItem.phoneWork, true)
      ? FormatService.formatPhoneValue(userFormItem.phoneWork) : null;
    userFormItem.phoneWorkExtension = userFormItem.phoneWorkExtension
      ? FormatService.parseInt(userFormItem.phoneWorkExtension) : null;
    userFormItem.isPhoneVerified = userFormItem.isPhoneVerified || userFormItem.isPhoneVerified === false
      ? userFormItem.isPhoneVerified : false;
    userFormItem.companyId = contactCompany ? contactCompany.id : (userFormItem.companyId || null);

    if (userPhotoFile) {
      userFormItem.photo = userPhotoFile;
    } else if (isUserPhotoDeleted) {
      userFormItem.photo = null;
    } else {
      delete userFormItem.photo;
    }

    if (!userFormItem.verificationTypeDefault) {
      userFormItem.verificationTypeDefault = null;
    }

    const userItem: UserInput = userFormItem;
    const notificationSettings: SettingInput[] = notificationSettingsForm;
    const userSettings: SettingInput[] = userSettingsForm;

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch((isUserProfile ? saveProfileSettings : saveUserSettingsData)({
        userForm: {
          user: userItem,
          notificationSettings,
          userSettings,
        },
      }, (response: ActionResponse): void => {
        onCancelForm();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [
    isSaveDisabled, userId, userPhotoFile, isUserPhotoDeleted, isUserProfile, isUserPhoneVerified, isUserApiKeyChanged,
    userNotificationSettingsData,
  ]);

  const onSubmitForm: Function = useCallback((): void => {
    if (submitForm) {
      submitForm();

      if (!withFormErrors) {
        sendForm(formValues);
      } else {
        FormService.focusOnFirstFormError();
      }
    }
  }, [
    isSaveDisabled, userId, userPhotoFile, isUserPhotoDeleted, isUserProfile, isUserPhoneVerified, isUserApiKeyChanged,
    formValues, withFormErrors,
  ]);

  const assetTypesChange: Function = useCallback((assetTypeValues: AssetType[]): void => {
    setUserAssetTypes(assetTypeValues);

    if (onAssetTypesChange) {
      onAssetTypesChange(assetTypeValues);
    }
  }, []);

  const closeUserSessionsConfirmModal: Function = useCallback((): void => {
    setIsCloseUserSessionsConfirmModalOpen(false);
    setConfirmModalErrorMessage(null);
  }, []);

  const closeResetSettingsConfirmModal: Function = useCallback((): void => {
    setIsResetSettingsConfirmModalOpen(false);
    setConfirmModalErrorMessage(null);
  }, []);

  const onCloseUserSessions: Function = useCallback((): void => {
    setIsCloseUserSessionsConfirmModalOpen(true);
  }, []);

  const onResetSettings: Function = useCallback((): void => {
    setIsResetSettingsConfirmModalOpen(true);
  }, []);

  const closeVerifyPhoneModal: Function = useCallback((): void => {
    setIsVerifyPhoneModalOpen(false);
    setVerifyPhoneModalErrorMessage(null);
    setUserPhone(null);
  }, []);

  const onVerifyPhoneConfirm: Function = useCallback((
    verificationCode: string, phone: string, onSuccess?: Function,
  ): void => {
    if (!verificationCode || !phone || !actionHash) {
      return;
    }

    dispatch(verifyUserPhone({
      verificationForm: {
        code: verificationCode,
        phone,
        actionHash,
      },
      userId: userId || null,
    }, (): void => {
      closeVerifyPhoneModal();

      setStatusMessage('Verified successfully');
      setIsOpenMessage(true);

      if (onSuccess) {
        onSuccess();
      }
    }, (exception: ServerError): void => {
      if (exception && exception.message) {
        setVerifyPhoneModalErrorMessage(exception.message);
      }
    }));
  }, [actionHash, userId]);

  const onExportSettings: Function = useCallback((): Promise<ActionCallback> | null => {
    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(exportUserSettings({
        userId,
      }, (response: ActionResponse): void => {
        setImportExportErrorMessage(null);

        setStatusMessage('Exported successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setImportExportErrorMessage(exception.message);
        }
      }));
    });
  }, [userId]);

  const onImportSettings: Function = useCallback((
    importFile: File, onSuccess?: Function,
  ): Promise<ActionCallback> | null => {
    if (!userId || !importFile) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(importUserSettings({
        userId,
        importFile,
      }, (response: ActionResponse): void => {
        setImportExportErrorMessage(null);

        setStatusMessage('Imported successfully');
        setIsOpenMessage(true);

        if (onSuccess) {
          onSuccess();
        }

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setImportExportErrorMessage(exception.message);
        }
      }));
    });
  }, [userId]);

  const onGetUserApiKey: Function = useCallback((): void => {
    dispatch(getUserApiKey({
      userId,
    }));
  }, [userId]);

  const onCopyUserApiKey: Function = useCallback((): void => {
    setStatusMessage('Copied to clipboard');
    setIsOpenMessage(true);
  }, []);

  const onImportSettingsFileDelete: Function = useCallback((): void => {
    setImportExportErrorMessage(null);
  }, []);

  const onCloseUserSessionsConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!userId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(closeUserSessions({
        userId,
      }, (response: ActionResponse): void => {
        setIsCloseUserSessionsConfirmModalOpen(false);
        setConfirmModalErrorMessage(null);

        setStatusMessage('All other sessions were terminated successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [userId]);

  const onResetSettingsConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!userId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(resetUserSettings({
        userId,
      }, (response: ActionResponse): void => {
        setIsResetSettingsConfirmModalOpen(false);
        setConfirmModalErrorMessage(null);

        setStatusMessage('Settings were reset to defaults successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [userId]);

  const onDefaultPortfolioValuesChange: Function = useCallback((portfolioValues: Portfolio[]): void => {
    setSelectedDefaultPortfolios(portfolioValues);

    if (changeForm) {
      changeForm(`userSettings[${SETTING_NAME_DEFAULT_PORTFOLIO_FILTER_IDS}].value`, portfolioValues);
    }
  }, []);

  const onSearchResultGroupValuesChange: Function = useCallback((
    searchResultGroupValues: SearchResultGroup[],
  ): void => {
    setSelectedSearchResultGroups(searchResultGroupValues);

    if (changeForm) {
      changeForm(`userSettings[${SETTING_NAME_SEARCH_RESULT_GROUPS}].value`, searchResultGroupValues);
    }
  }, []);

  const notificationSettingOptionDealsChange: Function = useCallback((
    dealValues: Deal[], settingName: string, settingId: number,
  ): void => {
    setSelectedNotificationDeals({
      ...selectedNotificationDeals,
      [settingId]: dealValues,
    });

    if (onNotificationSettingOptionDealsChange) {
      onNotificationSettingOptionDealsChange(settingName, dealValues);
    }
  }, [selectedNotificationDeals]);

  const notificationSettingOptionPortfoliosChange: Function = useCallback((
    portfolioValues: Portfolio[], settingName: string, settingId: number,
  ): void => {
    setSelectedNotificationPortfolios({
      ...selectedNotificationPortfolios,
      [settingId]: portfolioValues,
    });

    if (onNotificationSettingOptionPortfoliosChange) {
      onNotificationSettingOptionPortfoliosChange(settingName, portfolioValues);
    }
  }, [selectedNotificationPortfolios]);

  const onNotificationVisibleOptionsChange: Function = useCallback((newVisibleOptions: number[]): void => {
    setNotificationVisibleOptions(newVisibleOptions);
  }, []);

  const resetSelectedNotificationDeals: Function = useCallback((): void => {
    setSelectedNotificationDeals(
      userNotificationSettingsData.reduce((
        resultSelectedDeals: Record<string, Deal[]>, notificationSettingItem: Setting,
      ): Record<string, Deal[]> => (
        notificationSettingItem.options && notificationSettingItem.options.dealIds
        && notificationSettingItem.options.dealIds.length
          ? {
            ...resultSelectedDeals,
            [notificationSettingItem.id]: notificationSettingItem.options.dealIds.map((dealId: number): Deal | null => (
              dealList && dealList.length ? (
                dealList.find((dealItem: Deal): boolean => dealItem.id === dealId) || null
              ) : null
            )).filter((dealItem: Deal | null): boolean => !!dealItem) as Deal[],
          } : resultSelectedDeals
      ), {}),
    );
  }, [userNotificationSettingsData, dealList]);

  const resetSelectedNotificationPortfolios: Function = useCallback((): void => {
    setSelectedNotificationPortfolios(
      userNotificationSettingsData.reduce((
        resultSelectedPortfolios: Record<string, Portfolio[]>, notificationSettingItem: Setting,
      ): Record<string, Portfolio[]> => (
        notificationSettingItem.options && notificationSettingItem.options.portfolioIds
        && notificationSettingItem.options.portfolioIds.length
          ? {
            ...resultSelectedPortfolios,
            [notificationSettingItem.id]: notificationSettingItem.options.portfolioIds.map((
              portfolioId: number,
            ): Portfolio | null => (
              portfolioList && portfolioList.length ? (
                portfolioList.find((portfolioItem: Portfolio): boolean => portfolioItem.id === portfolioId) || null
              ) : null
            )).filter((portfolioItem: Portfolio): boolean => !!portfolioItem),
          } : resultSelectedPortfolios
      ), {}),
    );
  }, [userNotificationSettingsData, portfolioList]);

  const resetNotificationVisibleOptions: Function = useCallback((): void => {
    setNotificationVisibleOptions(
      userNotificationSettingsData.filter((settingItem: Setting): boolean => !!settingItem.value)
        .map((settingItem: Setting): number => settingItem.id),
    );
  }, [userNotificationSettingsData]);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const userPhotoDelete: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setIsUserPhotoDeleted(true);
    setUserPhotoFile(null);

    if (onUserPhotoChange) {
      onUserPhotoChange(null);
    }

    event.stopPropagation();
  }, []);

  const userPhotoChange: Function = useCallback((file: File): void => {
    setIsUserPhotoDeleted(false);
    setUserPhotoFile(file);

    if (onUserPhotoChange) {
      onUserPhotoChange(file);
    }
  }, []);

  const onFormLeave: FormLeaveFunction = useCallback((nextLocation: ReactRouterLocation): boolean => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastLocation(nextLocation);

      return false;
    }

    if (isEditMode) {
      dispatch(toggleEditMode(false));
    }

    if (isUserApiKeyChanged) {
      dispatch(resetUserApiKey());
    }

    return true;
  }, [isFormChanged, isUserApiKeyChanged, isEditMode]);

  const onBeforeLocationChange: Function = useCallback((nextLocation: ReactRouterLocation): void => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastLocation(nextLocation);
    }
  }, [isFormChanged]);

  const onBeforeSubTabChange: Function = useCallback((newTabIndex: number): void => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastTabIndex(newTabIndex);
    }
  }, [isFormChanged]);

  const onConnectOtpData: Function = useCallback((): void => {
    dispatch(generateOtpSecretData({
      userId,
    }, (): void => {
      setConnectOtpModalErrorMessage(null);
    }, (exception: ServerError): void => {
      if (exception && exception.message) {
        setConnectOtpModalErrorMessage(exception.message);
      }
    }));
  }, []);

  const onVerifyOtpSecretData: Function = useCallback((verificationCode: string, onSuccess: Function): void => {
    if (!verificationCode) {
      return;
    }

    dispatch(verifyUserOtp({
      verificationForm: {
        code: verificationCode,
      },
      userId,
    }, (): void => {
      setStatusMessage('Verified successfully');
      setIsOpenMessage(true);
      setConnectOtpModalErrorMessage(null);

      if (onSuccess) {
        onSuccess();
      }
    }, (exception: ServerError): void => {
      if (exception && exception.message) {
        setConnectOtpModalErrorMessage(exception.message);
      }
    }));
  }, []);

  const onDeleteOtpSecretData: Function = useCallback((onSuccess?: Function): void => {
    dispatch(deleteOtpSecretData({
      userId,
    }, (): void => {
      setStatusMessage('Authenticator data deleted successfully');
      setIsOpenMessage(true);

      if (userData.verificationTypeDefault === VERIFICATION_TYPE_AUTHENTICATOR_APP_OTP) {
        changeForm('verificationTypeDefault', '');
      }

      if (onSuccess) {
        onSuccess();
      }
    }));
  }, [userData]);

  const onGetOtpSecretData: Function = useCallback((): void => {
    dispatch(getOtpSecretData({
      userId,
    }, (): void => {
      setConnectOtpModalErrorMessage(null);
    }, (exception: ServerError): void => {
      if (exception && exception.message) {
        setConnectOtpModalErrorMessage(exception.message);
      }
    }));
  }, []);

  const tabs: TabItem[] = [
    {
      id: 1,
      name: TAB_NAME_GENERAL,
      title: 'General',
      component: (
        <SettingsUserGeneral
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          isUserProfile={isUserProfile}
          isFormChanged={isFormChanged}
          formError={formError}
          userData={userData}
          selectedDefaultPortfolios={selectedDefaultPortfolios}
          selectedSearchResultGroups={selectedSearchResultGroups}
          assetTypeList={assetTypeList}
          portfolioList={portfolioList}
          routeList={routeList}
          roleList={roleList}
          contactCompanyList={contactCompanyList}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onAssetTypesChange={assetTypesChange}
          onAddressSearchCompleted={onAddressSearchCompleted}
          onAddressFieldKeyDown={onAddressFieldKeyDown}
          onPhoneChange={onPhoneChange}
          onPhoneWorkChange={onPhoneWorkChange}
          onVerifyPhone={onVerifyPhone}
          onContactCompanySearch={onContactCompanySearch}
          onContactCompanySearchClose={onContactCompanySearchClose}
          onDefaultPortfolioValuesChange={onDefaultPortfolioValuesChange}
          onSearchResultGroupValuesChange={onSearchResultGroupValuesChange}
          onBeforeLocationChange={onBeforeLocationChange}
        />
      ),
    },
    {
      id: 2,
      name: TAB_NAME_SECURITY,
      title: 'User Security',
      component: (
        <SettingsUserSecuritySummary
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          isFormChanged={isFormChanged}
          isUserApiKeyChanged={isUserApiKeyChanged}
          formError={formError}
          currentUser={currentUser}
          userData={userData}
          otpSecretHash={otpSecretHash}
          otpSecretQrCodeLink={otpSecretQrCodeLink}
          importExportErrorMessage={importExportErrorMessage}
          connectOtpModalErrorMessage={connectOtpModalErrorMessage}
          dealList={dealList}
          portfolioList={portfolioList}
          hiddenPortfolioList={hiddenPortfolioList}
          accessLevelList={accessLevelList}
          accountUserTypeList={accountUserTypeList}
          verificationTypeList={verificationTypeList}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onResetSettings={onResetSettings}
          onExportSettings={onExportSettings}
          onImportSettings={onImportSettings}
          onGetUserApiKey={onGetUserApiKey}
          onCopyUserApiKey={onCopyUserApiKey}
          onConnectOtpData={onConnectOtpData}
          onVerifyOtpSecretData={onVerifyOtpSecretData}
          onDeleteOtpSecretData={onDeleteOtpSecretData}
          onGetOtpSecretData={onGetOtpSecretData}
          onVerifyPhone={onVerifyPhone}
          onAccountUserDealsChange={onAccountUserDealsChange}
          onAccountUserPortfoliosChange={onAccountUserPortfoliosChange}
          onAccountUserTypeChange={onAccountUserTypeChange}
          onAccountUserDealAccessLevelChange={onAccountUserDealAccessLevelChange}
          onImportSettingsFileDelete={onImportSettingsFileDelete}
          onDealSearch={onDealSearch}
          onDealSearchClose={onDealSearchClose}
        />
      ),
    },
    {
      id: 3,
      name: TAB_NAME_ACCOUNT_ACTIVITY,
      title: 'User Activity',
      component: (
        <SettingsUserAccountActivity
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          formError={formError}
          userData={userData}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onCloseUserSessions={onCloseUserSessions}
        />
      ),
    },
    {
      id: 4,
      name: TAB_NAME_NOTIFICATIONS,
      title: 'User Notifications',
      component: (
        <SettingsUserNotifications
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          isFormChanged={isFormChanged}
          formError={formError}
          notificationSettingsData={userNotificationSettingsData}
          visibleOptions={notificationVisibleOptions}
          selectedDeals={selectedNotificationDeals}
          selectedPortfolios={selectedNotificationPortfolios}
          dealList={commonDealList}
          portfolioList={commonPortfolioList}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onOptionDealsChange={notificationSettingOptionDealsChange}
          onOptionPortfoliosChange={notificationSettingOptionPortfoliosChange}
          onVisibleOptionsChange={onNotificationVisibleOptionsChange}
          onDealSearch={onDealSearch}
          onDealSearchClose={onDealSearchClose}
          onBeforeLocationChange={onBeforeLocationChange}
        />
      ),
    },
  ];

  const onTabChange: Function = useCallback((newTabId: number): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    onCancelForm();

    if (userData) {
      tabs.forEach((tabItem: TabItem): void => {
        if (tabItem.id === newTabId) {
          queryParams[QUERY_PARAM_TAB] = tabItem.name;

          navigate({
            pathname: `${ROUTE_USER}/${userData.id}`,
            search: FormatService.formatQueryParams(queryParams),
          });
        }
      });
    }
  }, [locationQueryParams, tabs, userData, isUserPhoneVerified, isUserApiKeyChanged]);

  const tabItemGeneral: TabItem | null = tabs && tabs.length ? tabs.find((tab: TabItem): boolean => (
    tab.name === TAB_NAME_GENERAL
  )) || null : null;
  const tabItemGeneralComponent: ReactElement | null = tabItemGeneral && tabItemGeneral.component
    ? tabItemGeneral.component : null;

  useEffect((): void => {
    if (isLeaveConfirmed) {
      if (lastLocation) {
        navigate({
          pathname: lastLocation.pathname,
          search: lastLocation.search || '',
        });

        setLastLocation(undefined);
      }

      if (lastTabIndex !== null && lastTabIndex >= TAB_INDEX_DEFAULT) {
        const queryParams: LocationQueryParams = {};
        const currentTab: TabItem = tabs[lastTabIndex];

        if (currentTab) {
          queryParams[QUERY_PARAM_TAB] = currentTab.name;

          navigate({
            pathname: `${ROUTE_USER}/${userId}`,
            search: FormatService.formatQueryParams(queryParams),
          });
        }

        setLastTabIndex(null);
      }

      setIsLeaveConfirmed(false);
    }
  }, [isLeaveConfirmed]);

  useEffect((): void => {
    if (lastLocation) {
      setLastTabIndex(null);
    }
  }, [lastLocation]);

  useEffect((): void => {
    if (userSettingsExportFile && userSettingsExportFile.link) {
      ScreenService.downloadFile(userSettingsExportFile.link);
      dispatch(resetUserSettingsExportFile());
    }
  }, [userSettingsExportFile]);

  useEffect((): void => {
    if (userNotificationSettingsData && userNotificationSettingsData.length) {
      if (!selectedNotificationDeals) {
        resetSelectedNotificationDeals();
      }

      if (!selectedNotificationPortfolios) {
        resetSelectedNotificationPortfolios();
      }

      if (!notificationVisibleOptions) {
        resetNotificationVisibleOptions();
      }
    }
  }, [userNotificationSettingsData]);

  useEffect((): void => {
    if (userSettingsData) {
      if (!selectedDefaultPortfolios) {
        const defaultPortfolioFilterIds: number[] = userSettingsData[SETTING_NAME_DEFAULT_PORTFOLIO_FILTER_IDS];

        setSelectedDefaultPortfolios(
          portfolioList && portfolioList.length && defaultPortfolioFilterIds && defaultPortfolioFilterIds.length
            ? defaultPortfolioFilterIds.map((portfolioId: number): Portfolio | null => (
              portfolioList.find((portfolioItem: Portfolio): boolean => portfolioItem.id === portfolioId)
            )).filter((portfolioItem: Portfolio | null): boolean => !!portfolioItem) : [],
        );
      }

      if (!selectedSearchResultGroups) {
        const searchResultGroups: string[] = userSettingsData[SETTING_NAME_SEARCH_RESULT_GROUPS];

        setSelectedSearchResultGroups(
          searchResultGroups && searchResultGroups.length
            ? searchResultGroups.map((searchResultGroupName: string): SearchResultGroup | null => (
              SEARCH_RESULT_GROUP_LIST.find((searchResultGroupItem: SearchResultGroup): boolean => (
                searchResultGroupItem.name === searchResultGroupName
              )) || null
            )).filter((searchResultGroupItem: SearchResultGroup | null): boolean => !!searchResultGroupItem) : [],
        );
      }
    }
  }, [userId, userSettingsTotal]);

  useEffect((): void => {
    if (userData && (isUserPhoneVerified === null || userIsPhoneVerified !== isUserPhoneVerified)) {
      setIsUserPhoneVerified(userIsPhoneVerified);
    }
  }, [userData, userIsPhoneVerified]);

  useEffect((): void => {
    if (currentUserApiKey !== userApiKeyValue) {
      if (changeForm && currentUserApiKey !== null) {
        changeForm('apiKey', userApiKeyValue);
      }

      setCurrentUserApiKey(userApiKeyValue);
    }
  }, [userApiKeyValue]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  useEditModeQuery(isEditModeQueryDisabled);

  useFormBlock(onFormLeave, isFormChanged, isFormStateChanged, isLeaveConfirmed, isLeaveConfirmModalOpen);

  return (
    <Fragment>
      <PullToRefresh isDisabled={isEditMode} onRefresh={onRefresh}>
        <ContentHeader title={pageTitle} />
        <SettingsPageTabs
          isDisabled={isEditMode}
          isCompressed={isRightDrawerOpen}
          activeTab={activeTab}
          currentUser={currentUser}
          onBeforeLocationChange={onBeforeLocationChange}
        />
        {
          !isDataLoaded && (
            <Loader isPageLoader withTopOffset />
          )
        }
        {
          isDataLoaded && userData && (
            <Fragment>
              <Controls
                withDelete={!isUserProfile}
                isSaveDisabled={isSaveDisabled}
                withNoEditMode={withNoEditMode}
                deletePermission={SETTINGS_USER_DELETE}
                editPermissions={editPermissions}
                onCancelForm={onCancelForm}
                onSubmitForm={submitForm}
                onDeleteClick={onDeleteClick}
              />
              <PageContainer>
                <PageContainerSide>
                  <ImageContainer
                    isEditMode={isEditMode}
                    isImageDeleted={isUserPhotoDeleted}
                    name={userData.fullName}
                    image={userData.photo}
                    imageThumbnail={userData.photoThumbnail}
                    imageFile={userPhotoFile}
                    imageFormFieldName="photo"
                    defaultImage={PersonDefaultImage}
                    onImageFileChange={userPhotoChange}
                    onImageFileDelete={userPhotoDelete}
                  />
                </PageContainerSide>
                <PageContainerMain>
                  {
                    !isUserProfile ? (
                      <LimitedPageContainer isActive>
                        <PageTabs
                          isSubTabs
                          isDisabled={isFormChanged}
                          isCompressed={isRightDrawerOpen}
                          tabs={tabs}
                          currentTab={queryTab}
                          onTabChange={onTabChange}
                          onBeforeTabChange={onBeforeSubTabChange}
                        />
                      </LimitedPageContainer>
                    ) : (
                      tabItemGeneralComponent
                    )
                  }
                </PageContainerMain>
              </PageContainer>
            </Fragment>
          )
        }
        {
          (!!formError || !!mainServerFormError) && !isFormSaving && (
            <FormError
              withShowError={isEditMode}
              withLeftOffset={isRightDrawerOpen}
              error={formError || mainServerFormError}
            />
          )
        }
      </PullToRefresh>
      <VerifyPhoneModal
        isOpen={isVerifyPhoneModalOpen}
        phone={userPhone}
        errorMessage={verifyPhoneModalErrorMessage}
        onVerifyPhoneConfirm={onVerifyPhoneConfirm}
        onCloseModal={closeVerifyPhoneModal}
      />
      <ConfirmModal
        isOpen={isDeleteConfirmModalOpen}
        title={deleteConfirmModalTitle}
        text={deleteConfirmModalText}
        errorMessage={confirmModalErrorMessage}
        onConfirm={onDeleteConfirm}
        onCloseModal={closeDeleteConfirmModal}
      />
      <ConfirmModal
        isOpen={isCloseUserSessionsConfirmModalOpen}
        title={closeUserSessionsConfirmModalTitle}
        text={closeUserSessionsConfirmModalText}
        errorMessage={confirmModalErrorMessage}
        onConfirm={onCloseUserSessionsConfirm}
        onCloseModal={closeUserSessionsConfirmModal}
      />
      <ConfirmModal
        isOpen={isResetSettingsConfirmModalOpen}
        title={resetSettingsConfirmModalTitle}
        text={resetSettingsConfirmModalText}
        errorMessage={confirmModalErrorMessage}
        onConfirm={onResetSettingsConfirm}
        onCloseModal={closeResetSettingsConfirmModal}
      />
      <LeaveConfirmModal
        isOpen={isLeaveConfirmModalOpen}
        onConfirm={onLeaveConfirm}
        onCloseModal={closeLeaveConfirmModal}
      />
      <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
    </Fragment>
  );
});

export default SettingsUserInternal;
